/**
 * Super Admin Subscriptions Page - 구독 현황 관리
 * 
 * 전체 구독 현황을 한눈에 파악
 * 기본 필터: 유료만
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
    CreditCard, Users, TrendingUp, Loader2, Search, Building2, ExternalLink
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { Fire } from '../../services/FireClient';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';

interface WorkspaceSubscription {
    workspaceId: string;
    workspaceName: string;
    tier: string;
    status: 'active' | 'past_due' | 'canceled' | 'none';
    amount: number;
    currency: string;
    startDate?: Date;
    endDate?: Date;
    memberCount: number;
}

interface SubscriptionSummary {
    total: number;
    paid: number;
    free: number;
    active: number;
    pastDue: number;
    canceled: number;
    mrr: number;
}

const SuperAdminSubscriptionsPage: React.FC = () => {
    const { i18n } = useLingui();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [workspaces, setWorkspaces] = useState<WorkspaceSubscription[]>([]);
    const [summary, setSummary] = useState<SubscriptionSummary>({
        total: 0,
        paid: 0,
        free: 0,
        active: 0,
        pastDue: 0,
        canceled: 0,
        mrr: 0,
    });
    const [filter, setFilter] = useState<'paid' | 'free' | 'all'>('paid');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'past_due' | 'canceled'>('all');
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        loadSubscriptions();
    }, []);

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const workspacesSnap = await getDocs(collection(Fire.store, 'workspaces'));

            const subscriptions: WorkspaceSubscription[] = [];

            for (const wsDoc of workspacesSnap.docs) {
                const wsData = wsDoc.data();

                // 현재 구독 정보 (overview 캐시 또는 직접 조회)
                const overview = wsData.overview?.currentSubscription;
                const tier = wsData.tier || 'FREE';
                const isFree = tier === 'FREE';

                subscriptions.push({
                    workspaceId: wsDoc.id,
                    workspaceName: wsData.name || wsDoc.id,
                    tier,
                    status: overview?.status || (isFree ? 'none' : 'active'),
                    amount: overview?.amount || 0,
                    currency: overview?.currency || 'JPY',
                    startDate: overview?.startDate?.toDate?.(),
                    endDate: overview?.endDate?.toDate?.(),
                    memberCount: wsData.memberIds?.length || 1,
                });
            }

            setWorkspaces(subscriptions);

            // 요약 계산
            const paidWorkspaces = subscriptions.filter(w => w.tier !== 'FREE');
            const newSummary: SubscriptionSummary = {
                total: subscriptions.length,
                paid: paidWorkspaces.length,
                free: subscriptions.filter(w => w.tier === 'FREE').length,
                active: paidWorkspaces.filter(w => w.status === 'active').length,
                pastDue: paidWorkspaces.filter(w => w.status === 'past_due').length,
                canceled: paidWorkspaces.filter(w => w.status === 'canceled').length,
                mrr: paidWorkspaces.filter(w => w.status === 'active').reduce((sum, w) => sum + w.amount, 0),
            };
            setSummary(newSummary);

        } catch (e) {
            console.error('Failed to load subscriptions:', e);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'JPY') => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
    };

    const formatDate = (date: Date | undefined) => {
        return date ? date.toLocaleDateString('ja-JP') : '-';
    };

    // 필터링된 목록
    const filteredWorkspaces = useMemo(() => {
        return workspaces
            .filter(ws => {
                // 유료/무료 필터
                if (filter === 'paid' && ws.tier === 'FREE') return false;
                if (filter === 'free' && ws.tier !== 'FREE') return false;

                // 상태 필터 (유료만)
                if (filter === 'paid' && statusFilter !== 'all' && ws.status !== statusFilter) return false;

                // 검색
                if (searchQuery) {
                    const query = searchQuery.toLowerCase();
                    return ws.workspaceName.toLowerCase().includes(query) ||
                        ws.workspaceId.toLowerCase().includes(query);
                }
                return true;
            })
            .sort((a, b) => b.amount - a.amount);
    }, [workspaces, filter, statusFilter, searchQuery]);

    const getStatusBadge = (status: string, tier: string) => {
        if (tier === 'FREE') {
            return <Badge className="bg-slate-100 text-slate-600">FREE</Badge>;
        }
        switch (status) {
            case 'active':
                return <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>;
            case 'past_due':
                return <Badge className="bg-red-100 text-red-700">Past Due</Badge>;
            case 'canceled':
                return <Badge className="bg-slate-100 text-slate-600">Canceled</Badge>;
            default:
                return <Badge className="bg-slate-100 text-slate-600">-</Badge>;
        }
    };

    const getTierBadge = (tier: string) => {
        const colors: Record<string, string> = {
            'FREE': 'bg-slate-100 text-slate-600',
            'PREMIUM': 'bg-indigo-100 text-indigo-700',
            'BUSINESS': 'bg-purple-100 text-purple-700',
            'ENTERPRISE': 'bg-amber-100 text-amber-700',
        };
        return <Badge className={colors[tier] || 'bg-slate-100 text-slate-600'}>{tier}</Badge>;
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 sticky top-0 z-10 px-6 py-4">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold text-slate-900">
                        <Trans>サブスクリプション管理</Trans>
                    </h1>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 space-y-6">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <>
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-indigo-100 rounded-lg">
                                            <TrendingUp className="w-5 h-5 text-indigo-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{formatCurrency(summary.mrr)}</div>
                                            <div className="text-xs text-slate-500">MRR (月額収益)</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-100 rounded-lg">
                                            <CreditCard className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.paid}</div>
                                            <div className="text-xs text-slate-500">有料ワークスペース</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-slate-100 rounded-lg">
                                            <Building2 className="w-5 h-5 text-slate-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.free}</div>
                                            <div className="text-xs text-slate-500">無料ワークスペース</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 rounded-lg">
                                            <Users className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.total}</div>
                                            <div className="text-xs text-slate-500">全ワークスペース</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Filter & Search */}
                        <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                                <Button
                                    variant={filter === 'paid' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilter('paid')}
                                >
                                    有料 ({summary.paid})
                                </Button>
                                <Button
                                    variant={filter === 'free' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilter('free')}
                                >
                                    無料 ({summary.free})
                                </Button>
                                <Button
                                    variant={filter === 'all' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilter('all')}
                                >
                                    全て ({summary.total})
                                </Button>

                                {filter === 'paid' && (
                                    <>
                                        <div className="w-px h-6 bg-slate-200 mx-2" />
                                        <Button
                                            variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
                                            size="sm"
                                            onClick={() => setStatusFilter('all')}
                                        >
                                            全状態
                                        </Button>
                                        <Button
                                            variant={statusFilter === 'active' ? 'secondary' : 'ghost'}
                                            size="sm"
                                            onClick={() => setStatusFilter('active')}
                                        >
                                            Active ({summary.active})
                                        </Button>
                                        <Button
                                            variant={statusFilter === 'past_due' ? 'secondary' : 'ghost'}
                                            size="sm"
                                            onClick={() => setStatusFilter('past_due')}
                                        >
                                            Past Due ({summary.pastDue})
                                        </Button>
                                    </>
                                )}
                            </div>

                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    placeholder="検索..."
                                    className="pl-9 w-64"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Workspace List */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">
                                    ワークスペース一覧
                                    <span className="ml-2 text-sm font-normal text-slate-500">
                                        ({filteredWorkspaces.length}件)
                                    </span>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {filteredWorkspaces.length === 0 ? (
                                    <div className="text-center py-12 text-slate-400">
                                        該当するワークスペースがありません
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredWorkspaces.map(ws => (
                                            <div
                                                key={ws.workspaceId}
                                                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                                                onClick={() => navigate(`/super-admin/workspaces/${ws.workspaceId}`)}
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <div className="font-medium text-slate-900 flex items-center gap-2">
                                                            {ws.workspaceName}
                                                            <ExternalLink className="w-3 h-3 text-slate-400" />
                                                        </div>
                                                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                                                            <Users className="w-3 h-3" />
                                                            {ws.memberCount}명
                                                            {ws.startDate && (
                                                                <>
                                                                    <span className="mx-1">·</span>
                                                                    開始: {formatDate(ws.startDate)}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-4">
                                                    {getTierBadge(ws.tier)}
                                                    {getStatusBadge(ws.status, ws.tier)}
                                                    {ws.tier !== 'FREE' && (
                                                        <div className="text-right">
                                                            <div className="font-bold text-slate-900">
                                                                {formatCurrency(ws.amount, ws.currency)}
                                                            </div>
                                                            <div className="text-xs text-slate-500">/月</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
};

export default SuperAdminSubscriptionsPage;
